import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VitalSignsChart = ({ vitals = [] }) => {
  const getVitalIcon = (type) => {
    switch (type) {
      case 'Blood Pressure':
        return 'Activity';
      case 'Heart Rate':
        return 'Heart';
      case 'Temperature':
        return 'Thermometer';
      case 'Respiratory Rate':
        return 'Wind';
      case 'Oxygen Saturation':
        return 'Droplet';
      case 'Weight':
        return 'Scale';
      default:
        return 'Activity';
    }
  };

  const isAbnormal = (vital) => {
    return vital?.flag === 'High' || vital?.flag === 'Low';
  };

  if (vitals?.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        <div className="flex items-center justify-center w-16 h-16 bg-muted rounded-full mb-4">
          <Icon name="Activity" size={32} color="var(--color-muted-foreground)" />
        </div>
        <p className="text-base font-medium text-foreground mb-2">No Vital Signs</p>
        <p className="text-sm text-muted-foreground mb-4">Patient vital signs will appear here</p>
        <Button variant="default" iconName="Plus" iconPosition="left">
          Record Vitals
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg md:text-xl font-semibold text-foreground">Latest Vital Signs</h3>
        <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
          Record Vitals
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {vitals?.map((vital) => (
          <div
            key={vital?.id}
            className={`bg-card border rounded-lg p-4 md:p-5 transition-smooth hover:shadow-elevation-2 ${
              isAbnormal(vital) ? 'border-warning' : 'border-border'
            }`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${
                  isAbnormal(vital) ? 'bg-warning/10' : 'bg-primary/10'
                }`}>
                  <Icon 
                    name={getVitalIcon(vital?.type)} 
                    size={20} 
                    color={isAbnormal(vital) ? 'var(--color-warning)' : 'var(--color-primary)'} 
                  />
                </div>
                <div>
                  <p className="text-xs md:text-sm text-muted-foreground">{vital?.type}</p>
                  <p className="text-xl md:text-2xl font-bold text-foreground">{vital?.value}</p>
                </div>
              </div>
              {isAbnormal(vital) && (
                <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
              )}
            </div>
            
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{vital?.timestamp}</span>
              {vital?.flag && (
                <span className={`px-2 py-0.5 rounded-full ${
                  vital?.flag === 'High' ? 'bg-destructive/10 text-destructive' :
                  vital?.flag === 'Low'? 'bg-warning/10 text-warning' : 'bg-success/10 text-success'
                }`}>
                  {vital?.flag}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="bg-muted rounded-lg p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon name="TrendingUp" size={20} color="var(--color-primary)" />
          <h4 className="text-base font-semibold text-foreground">Vital Signs Trend</h4>
        </div>
        <div className="bg-card rounded-lg p-8 text-center">
          <Icon name="BarChart3" size={48} color="var(--color-muted-foreground)" />
          <p className="text-sm text-muted-foreground mt-4">Trend chart visualization</p>
        </div>
      </div>
    </div>
  );
};

export default VitalSignsChart;